(() => {
  const MATCHING = {
    laneWeight: 0.70,
    wholeFabricWeight: 0.30,
    analysisMinPercentage: 0.75,
    baseRepresentationDistance: 0.05,
    baseForcePercentage: 4,
    baseTargetCoverage: 90,
    baseMaxColours: 12,
    visibleResults: 12
  };

  const MULTICOLOUR_VALIDATION = {
    familyMinPercentage: 1.5,
    neutralChromaThreshold: 0.025,
    hueMergeDegrees: 38,
    minFamilies: 3,
    minSecondaryFamilyMass: 10,
    representedDistance: 0.08,
    unsupportedGrace: 0.30,
    penaltyWeight: 0.12
  };

  const ANALYTICS = {
    searchTermMaxLength: 80,
    zeroResultDelay: 1000,
    pendingAddExpiry: 120000
  };

  let privacyApiPromise = null;

  const stateBySection = new WeakMap();

  function init(section) {
    if (!section || stateBySection.has(section)) return;

    const els = {
      modeButtons: [...section.querySelectorAll('[data-kf-mode-button]')],
      modePanels: [...section.querySelectorAll('[data-kf-mode-panel]')],
      input: section.querySelector('[data-kf-image-input]'),
      workspace: section.querySelector('[data-kf-workspace]'),
      canvas: section.querySelector('[data-kf-preview-canvas]'),
      analyse: section.querySelector('[data-kf-analyse]'),
      search: section.querySelector('[data-kf-product-search]'),
      suggestions: section.querySelector('[data-kf-suggestions]'),
      status: section.querySelector('[data-kf-status]'),
      matchArea: section.querySelector('[data-kf-match-area]'),
      source: section.querySelector('[data-kf-source]'),
      sourceImage: section.querySelector('[data-kf-source-image]'),
      sourceTitle: section.querySelector('[data-kf-source-title]'),
      sourceDetails: section.querySelector('[data-kf-source-details]'),
      sourceFooter: section.querySelector('.kf-colour-matcher__source-footer'),
      mobileSource: section.querySelector('[data-kf-mobile-source]'),
      mobileSourceImage: section.querySelector('[data-kf-mobile-source-image]'),
      mobileSourceTitle: section.querySelector('[data-kf-mobile-source-title]'),
      results: section.querySelector('[data-kf-results]'),
      closeGrid: section.querySelector('[data-kf-close-grid]'),
      closeNote: section.querySelector('[data-kf-close-note]')
    };

    if (!els.search || !els.closeGrid || !els.source) return;

    const state = {
      products: null,
      productsPromise: null,
      basicProductIds: null,
      basicProductsPromise: null,
      sourceProductId: null,
      basePalette: [],
      rankedResults: [],
      dismissedIds: new Set(),
      resultsCount: MATCHING.visibleResults,
      sourceType: '',
      sourceSku: '',
      sourceTitle: '',
      resultsViewKey: '',
      zeroResultTimer: null,
      trackedZeroQueries: new Set(),
      pendingMatcherAdd: null,

      basicCollections: String(section.dataset.basicCollections || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean),

      quickAddEnabled: section.dataset.quickAddEnabled === 'true',

      mobileQuickAddEnabled:
        section.dataset.mobileQuickAddEnabled === 'true',

      addToCartIconUrl:
        section.dataset.addToCartIcon || '',

      image: null,
      imageUrl: null,
      selection: null,
      drag: null
    };

    stateBySection.set(section, state);

    setupMobileSourceSticky(
      section,
      els,
      state
    );

    setupMatcherCartAnalytics(
      state
    );

    if (
      'ResizeObserver' in window &&
      els.matchArea
    ) {
      let lastLayoutWidth = -1;

      state.layoutObserver =
        new ResizeObserver(
          (entries) => {
            const width =
              entries[0]?.contentRect?.width || 0;

            if (
              Math.abs(
                width - lastLayoutWidth
              ) < 1
            ) {
              return;
            }

            lastLayoutWidth = width;

            syncSourceCardHeight(
              els
            );
          }
        );

      state.layoutObserver.observe(
        els.matchArea
      );
    }

    els.modeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const mode =
          button.dataset.kfModeButton;

        els.modeButtons.forEach((candidate) => {
          const active =
            candidate === button;

          candidate.classList.toggle(
            'is-active',
            active
          );

          candidate.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        });

        els.modePanels.forEach((panel) => {
          panel.hidden =
            panel.dataset.kfModePanel !== mode;
        });

        setStatus(els, '');
      });
    });

    if (
      els.input &&
      els.canvas &&
      els.workspace &&
      els.analyse
    ) {
      setupImageUpload(
        els,
        state
      );
    }

    els.search.addEventListener(
      'input',
      async () => {
        const query =
          els.search.value.trim();

        if (query.length < 2) {
          els.suggestions.hidden = true;
          els.suggestions.innerHTML = '';
          return;
        }

        try {
          setStatus(
            els,
            'Loading fabrics…'
          );

          const products =
            await loadProducts(state);

          setStatus(els, '');

          renderSuggestions(
            products,
            query,
            els,
            state
          );
        } catch (error) {
          console.error(error);

          setStatus(
            els,
            'Sorry, the fabric list could not be loaded.'
          );
        }
      }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          !section.contains(
            event.target
          )
        ) {
          return;
        }

        if (
          event.target.closest(
            '[data-kf-product-search], [data-kf-suggestions]'
          )
        ) {
          return;
        }

        els.suggestions.hidden = true;
      }
    );

    const prefetch = () =>
      loadProducts(state)
        .catch(() => {});

    if (
      'requestIdleCallback' in window
    ) {
      window.requestIdleCallback(
        prefetch,
        {
          timeout: 1800
        }
      );
    } else {
      window.setTimeout(
        prefetch,
        700
      );
    }
  }

  function setupMobileSourceSticky(
    section,
    els,
    state
  ) {
    if (
      !els.mobileSource ||
      !els.source
    ) {
      return;
    }

    let frame = null;

    const requestSync = () => {
      if (frame !== null) {
        return;
      }

      frame =
        window.requestAnimationFrame(
          () => {
            frame = null;

            if (
              !section.isConnected
            ) {
              return;
            }

            syncMobileSourceSticky(
              els
            );
          }
        );
    };

    state.mobileSourceStickySync =
      requestSync;

    window.addEventListener(
      'scroll',
      requestSync,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      requestSync,
      {
        passive: true
      }
    );

    requestSync();
  }

  function syncMobileSourceSticky(
    els
  ) {
    if (
      !els.mobileSource ||
      !els.source ||
      !els.matchArea
    ) {
      return;
    }

    const mobile =
      window.matchMedia(
        '(max-width: 749px)'
      ).matches;

    if (
      !mobile ||
      els.source.hidden ||
      els.matchArea.hidden
    ) {
      els.mobileSource.hidden = true;
      return;
    }

    const sourceCard =
      els.source.querySelector(
        '.kf-colour-matcher__source-card'
      );

    const results =
      els.results;

    if (
      !sourceCard ||
      !results ||
      results.hidden
    ) {
      els.mobileSource.hidden = true;
      return;
    }

    const sourceRect =
      sourceCard.getBoundingClientRect();

    const resultsRect =
      results.getBoundingClientRect();

    const stickyStyle =
      window.getComputedStyle(
        els.mobileSource
      );

    const stickyTop =
      Number.parseFloat(
        stickyStyle.top
      ) || 88;

    const stickyHeight =
      els.mobileSource.hidden
        ? 134
        : els.mobileSource
            .getBoundingClientRect()
            .height || 134;

    const sourcePassed =
      sourceRect.bottom <=
      stickyTop + 6;

    const resultsRemain =
      resultsRect.bottom >
      stickyTop +
        Math.min(
          stickyHeight,
          150
        );

    els.mobileSource.hidden =
      !(
        sourcePassed &&
        resultsRemain
      );
  }

  function setStatus(
    els,
    message
  ) {
    if (!els.status) return;

    els.status.textContent =
      message;

    els.status.hidden =
      !message;
  }

  function resetResults(state) {
    state.dismissedIds.clear();
    state.rankedResults = [];
    state.resultsViewKey = '';
  }

  function ensureCustomerPrivacyApi() {
    if (
      window.Shopify
        ?.customerPrivacy
    ) {
      return Promise.resolve(
        window.Shopify.customerPrivacy
      );
    }

    if (privacyApiPromise) {
      return privacyApiPromise;
    }

    if (
      typeof window.Shopify
        ?.loadFeatures !==
      'function'
    ) {
      return Promise.resolve(
        null
      );
    }

    privacyApiPromise =
      new Promise(
        (resolve) => {
          window.Shopify.loadFeatures(
            [
              {
                name:
                  'consent-tracking-api',
                version:
                  '0.1'
              }
            ],
            (error) => {
              if (error) {
                resolve(
                  null
                );
                return;
              }

              resolve(
                window.Shopify
                  ?.customerPrivacy ||
                  null
              );
            }
          );
        }
      );

    return privacyApiPromise;
  }

  async function matcherAnalyticsAllowed() {
    try {
      const privacy =
        await ensureCustomerPrivacyApi();

      return (
        typeof privacy
          ?.analyticsProcessingAllowed ===
          'function' &&
        privacy.analyticsProcessingAllowed() ===
          true
      );
    } catch (
      error
    ) {
      return false;
    }
  }

function getMatcherCookie(name) {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return match
    ? match.slice(name.length + 1)
    : '';
}

async function pushMatcherAnalytics(
  payload
) {
  if (
    !payload ||
    !payload.event ||
    !await matcherAnalyticsAllowed()
  ) {
    return;
  }

  const clientId =
    getMatcherCookie('_ga');

  const sessionId =
    getMatcherCookie('_ga_CVPM02SN1S');

  if (
    !clientId ||
    !sessionId
  ) {
    return;
  }

  const {
    event,
    ...params
  } = payload;

  try {
    await fetch(
      'https://koala-colour-matcher-ga-590498872983.australia-southeast2.run.app',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        keepalive: true,
        body: JSON.stringify({
          event_name: event,
          client_id: clientId,
          session_id: sessionId,
          params
        })
      }
    );
  } catch {
    // Analytics must never interrupt the matcher.
  }
}

  function sanitiseSearchTerm(
    raw
  ) {
    const value =
      String(
        raw ||
        ''
      )
        .trim()
        .replace(
          /\s+/g,
          ' '
        );

    if (
      value.length <
      2
    ) {
      return '';
    }

    const emailPattern =
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

    if (
      emailPattern.test(
        value
      )
    ) {
      return '';
    }

    const digits =
      value.replace(
        /\D/g,
        ''
      );

    if (
      digits.length >=
        8 &&
      /^[\d\s()+.\-]+$/.test(
        value
      )
    ) {
      return '';
    }

    return value.slice(
      0,
      ANALYTICS.searchTermMaxLength
    );
  }

  function matcherSourceParams(
    state
  ) {
    return {
      matcher_source_type:
        state.sourceType ||
        '',
      matcher_source_sku:
        state.sourceSku ||
        ''
    };
  }

  function trackMatcherStart(
    state
  ) {
    void pushMatcherAnalytics(
      {
        event:
          'colour_matcher_start',
        ...matcherSourceParams(
          state
        )
      }
    );
  }

  function trackMatcherSearch(
    query,
    product,
    state
  ) {
    const searchTerm =
      sanitiseSearchTerm(
        query
      );

    if (
      !searchTerm
    ) {
      return;
    }

    void pushMatcherAnalytics(
      {
        event: 'colour_matcher_search',
        search_term:
          searchTerm,
        selected_item_id:
          product.sku ||
          String(
            product.id ||
            ''
          ),
        ...matcherSourceParams(
          state
        )
      }
    );
  }

  function scheduleZeroResultTracking(
    query,
    els,
    state
  ) {
    if (
      state.zeroResultTimer
    ) {
      window.clearTimeout(
        state.zeroResultTimer
      );
    }

    const searchTerm =
      sanitiseSearchTerm(
        query
      );

    if (
      !searchTerm ||
      state.trackedZeroQueries.has(
        searchTerm.toLowerCase()
      )
    ) {
      return;
    }

    state.zeroResultTimer =
      window.setTimeout(
        () => {
          const current =
            sanitiseSearchTerm(
              els.search?.value
            );

          if (
            !current ||
            current.toLowerCase() !==
              searchTerm.toLowerCase()
          ) {
            return;
          }

          state.trackedZeroQueries.add(
            searchTerm.toLowerCase()
          );

          void pushMatcherAnalytics(
            {
              event:
                'colour_matcher_no_results',
              search_term:
                searchTerm
            }
          );
        },
        ANALYTICS.zeroResultDelay
      );
  }

  function trackResultsView(
    visible,
    state
  ) {
    if (
      !visible.length ||
      state.resultsViewKey
    ) {
      return;
    }

    state.resultsViewKey =
      [
        state.sourceType ||
          'unknown',
        state.sourceProductId ||
          'upload',
        visible
          .map(
            ({
              product
            }) =>
              product.id
          )
          .join(',')
      ].join('|');

    void pushMatcherAnalytics(
      {
        event:
          'colour_matcher_results_view',
        matcher_results_count:
          visible.length,
        ...matcherSourceParams(
          state
        )
      }
    );
  }

  function trackResultSelect(
    product,
    index,
    state
  ) {
    void pushMatcherAnalytics(
      {
        event:
          'colour_matcher_result_select',
        matcher_item_id:
          product.sku ||
          String(
            product.id ||
            ''
          ),
        matcher_item_name:
          product.title ||
          '',
        matcher_item_category:
          product.type ||
          '',
        matcher_result_position:
          Number.isFinite(
            index
          )
            ? index + 1
            : null,
        ...matcherSourceParams(
          state
        )
      }
    );
  }

  function markPendingMatcherAdd(
    product,
    index,
    state
  ) {
    state.pendingMatcherAdd =
      {
        product,
        index,
        expiresAt:
          Date.now() +
          ANALYTICS.pendingAddExpiry
      };
  }

  function setupMatcherCartAnalytics(
    state
  ) {
    document.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        const pending =
          state.pendingMatcherAdd;

        if (
          !pending
        ) {
          return;
        }

        if (
          Date.now() >
          pending.expiresAt
        ) {
          state.pendingMatcherAdd =
            null;
          return;
        }

        const promise =
          event.promise;

        if (
          !promise ||
          typeof promise.then !==
            'function'
        ) {
          return;
        }

        promise
          .then(
            ({
              detail
            }) => {
              if (
                detail?.didError
              ) {
                return;
              }

              const addedProductId =
                String(
                  detail
                    ?.productId ||
                    ''
                );

              if (
                !addedProductId ||
                addedProductId !==
                  String(
                    pending.product
                      ?.id ||
                      ''
                  )
              ) {
                return;
              }

              state.pendingMatcherAdd =
                null;

              void pushMatcherAnalytics(
                {
                  event:
                    'colour_matcher_add_to_cart',
                  matcher_item_id:
                    pending.product.sku ||
                    String(
                      pending.product.id ||
                      ''
                    ),
                  matcher_item_name:
                    pending.product.title ||
                    '',
                  matcher_result_position:
                    Number.isFinite(
                      pending.index
                    )
                      ? pending.index + 1
                      : null,
                  matcher_item_category:
                    pending.product.type ||
                    '',
                  matcher_added_quantity:
                    Number(
                      detail
                        ?.itemCount
                    ) ||
                    1,
                  ...matcherSourceParams(
                    state
                  )
                }
              );
            }
          )
          .catch(
            () => {}
          );
      }
    );
  }

  function createSourcePaletteDots(
    colours
  ) {
    if (!Array.isArray(colours)) {
      return null;
    }

    const usableColours = colours
      .filter((colour) =>
        /^#[0-9a-f]{6}$/i.test(
          colour?.hex || ''
        )
      )
      .slice(0, 8);

    if (!usableColours.length) {
      return null;
    }

    const palette =
      document.createElement('div');

    palette.className =
      'kf-colour-matcher__source-palette';

    palette.setAttribute(
      'aria-hidden',
      'true'
    );

    usableColours.forEach(
      (colour) => {
        const dot =
          document.createElement('span');

        dot.className =
          'kf-colour-matcher__source-palette-dot';

        dot.style.backgroundColor =
          colour.hex;

        palette.appendChild(dot);
      }
    );

    return palette;
  }

  function renderSource(
    els,
    imageUrl,
    title,
    cardDetails = null,
    sourceProduct = null,
    state = null
  ) {
    if (els.sourceImage) {
      if (imageUrl) {
        els.sourceImage.src =
          imageUrl;

        els.sourceImage.alt =
          title || 'Selected fabric';

        els.sourceImage.hidden =
          false;
      } else {
        els.sourceImage.hidden =
          true;
      }
    }

    if (els.sourceTitle) {
      els.sourceTitle.textContent =
        title || '';
    }

    if (els.mobileSourceImage) {
      if (imageUrl) {
        els.mobileSourceImage.src =
          imageUrl;

        els.mobileSourceImage.alt =
          title || 'Selected fabric';

        els.mobileSourceImage.hidden =
          false;
      } else {
        els.mobileSourceImage.hidden =
          true;
      }
    }

    if (els.mobileSourceTitle) {
      els.mobileSourceTitle.textContent =
        title || '';
    }

    if (els.sourceDetails) {
      els.sourceDetails.innerHTML = '';

      const details =
        createCardDetailsElement(
          cardDetails
        );

      if (details) {
        els.sourceDetails.appendChild(
          details
        );
      }

      if (
        !sourceProduct &&
        state?.basePalette?.length
      ) {
        const paletteDots =
          createSourcePaletteDots(
            state.basePalette
          );

        if (paletteDots) {
          els.sourceDetails.appendChild(
            paletteDots
          );
        }
      }
    }

    renderSourcePurchase(
      els,
      sourceProduct,
      state
    );

    els.source.style.removeProperty(
      '--kf-source-card-height'
    );

    els.source.hidden = false;

    if (els.matchArea) {
      els.matchArea.hidden = false;
    }

    if (
      state?.mobileSourceStickySync
    ) {
      state.mobileSourceStickySync();
    }
  }

  function renderSourcePurchase(
    els,
    product,
    state
  ) {
    if (!els.sourceFooter) return;

    els.sourceFooter.innerHTML = '';

    if (!product || !state) {
      els.sourceFooter.hidden = true;
      return;
    }

    const pricing =
      document.createElement(
        'div'
      );

    pricing.className =
      'kf-colour-matcher__card-pricing';

    const price =
      document.createElement(
        'span'
      );

    price.className =
      'kf-colour-matcher__card-price';

    price.textContent =
      product.price || '';

    pricing.appendChild(
      price
    );

    const unitText =
      formatUnitPrice(
        product
      );

    if (unitText) {
      const unit =
        document.createElement(
          'small'
        );

      unit.className =
        'kf-colour-matcher__card-unit-price';

      unit.textContent =
        unitText;

      pricing.appendChild(
        unit
      );
    }

    els.sourceFooter.appendChild(
      pricing
    );

    if (!product.available) {
      const soldOut =
        document.createElement(
          'span'
        );

      soldOut.className =
        'kf-colour-matcher__sold-out';

      soldOut.textContent =
        'Currently unavailable';

      els.sourceFooter.appendChild(
        soldOut
      );
    } else if (
      state.quickAddEnabled
    ) {
      const quickAdd =
        createNativeQuickAdd(
          product,
          state
        );

      if (quickAdd) {
        els.sourceFooter.appendChild(
          quickAdd
        );
      }
    }

    els.sourceFooter.hidden = false;
  }

  function normaliseCardDetails(
    details
  ) {
    const value =
      details &&
      typeof details === 'object'
        ? details
        : {};

    return {
      productType: String(
        value.product_type || ''
      ).trim(),

      composition: String(
        value.composition || ''
      ).trim(),

      structure: String(
        value.structure || ''
      ).trim(),

      measurementLabel: String(
        value.measurement_label || ''
      ).trim(),

      measurementValue: String(
        value.measurement_value || ''
      ).trim(),

      weight: String(
        value.weight || ''
      ).trim()
    };
  }

  function createCardDetailsElement(
    details
  ) {
    if (!details) return null;

    const value =
      normaliseCardDetails(
        details
      );

    const hasDetails =
      value.productType ||
      value.composition ||
      value.structure ||
      value.measurementValue ||
      value.weight;

    if (!hasDetails) {
      return null;
    }

    const wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      'kf-card-fabric-details';

    if (value.productType) {
      const line =
        document.createElement(
          'div'
        );

      line.textContent =
        value.productType;

      wrapper.appendChild(
        line
      );
    }

    if (
      value.composition ||
      value.structure
    ) {
      const line =
        document.createElement(
          'div'
        );

      line.textContent =
        [
          value.composition,
          value.structure
        ]
          .filter(Boolean)
          .join(' | ');

      wrapper.appendChild(
        line
      );
    }

    if (
      value.measurementValue ||
      value.weight
    ) {
      const line =
        document.createElement(
          'div'
        );

      line.className =
        'kf-card-fabric-details__measurements';

      if (value.measurementValue) {
        const measurement =
          document.createElement(
            'span'
          );

        measurement.textContent =
          `${value.measurementLabel || 'Width'}: ${value.measurementValue}`;

        line.appendChild(
          measurement
        );
      }

      if (
        value.measurementValue &&
        value.weight
      ) {
        const separator =
          document.createElement(
            'span'
          );

        separator.setAttribute(
          'aria-hidden',
          'true'
        );

        separator.textContent = '|';

        line.appendChild(
          separator
        );
      }

      if (value.weight) {
        const weight =
          document.createElement(
            'span'
          );

        weight.textContent =
          `Weight: ${value.weight}`;

        line.appendChild(
          weight
        );
      }

      wrapper.appendChild(
        line
      );
    }

    return wrapper;
  }

  function syncSourceCardHeight(
    els
  ) {
    if (
      !els.source ||
      els.source.hidden ||
      !els.closeGrid
    ) {
      return;
    }

    const cards =
      [
        ...els.closeGrid.querySelectorAll(
          '.kf-colour-matcher__card'
        )
      ];

    if (!cards.length) {
      return;
    }

    const sourceCard =
      els.source.querySelector(
        '.kf-colour-matcher__source-card'
      ) || els.source;

    els.source.style.removeProperty(
      '--kf-source-card-height'
    );

    cards.forEach(
      (card) => {
        card.style.removeProperty(
          'min-height'
        );
      }
    );

    window.requestAnimationFrame(
      () => {
        const layout =
          window.getComputedStyle(
            els.source
          );

        if (
          layout.position === 'static'
        ) {
          return;
        }

        const heights =
          cards.map(
            (card) =>
              card.getBoundingClientRect().height
          );

        heights.push(
          sourceCard.getBoundingClientRect().height
        );

        const height =
          Math.max(
            ...heights
          );

        if (height > 0) {
          const equalHeight =
            `${Math.ceil(height)}px`;

          els.source.style.setProperty(
            '--kf-source-card-height',
            equalHeight
          );

          cards.forEach(
            (card) => {
              card.style.minHeight =
                equalHeight;
            }
          );
        }
      }
    );
  }

  function setupImageUpload(
    els,
    state
  ) {
    els.input.addEventListener(
      'change',
      (event) => {
        const file =
          event.target.files?.[0];

        if (
          !file ||
          !file.type.startsWith(
            'image/'
          )
        ) {
          return;
        }

        if (state.imageUrl) {
          URL.revokeObjectURL(
            state.imageUrl
          );
        }

        state.imageUrl =
          URL.createObjectURL(file);

        state.image =
          new Image();

        state.image.onload = () => {
          prepareCanvas(
            els.canvas,
            state.image
          );

          state.selection =
            initialSelection(
              els.canvas
            );

          state.sourceProductId =
            null;
          state.sourceType =
            '';
          state.sourceSku =
            '';
          state.sourceTitle =
            '';

          state.basePalette =
            [];

          resetResults(state);

          els.workspace.hidden =
            false;

          if (els.matchArea) {
            els.matchArea.hidden =
              true;
          }

          if (els.source) {
            els.source.hidden =
              true;
          }

          if (els.results) {
            els.results.hidden =
              true;
          }

          els.closeGrid.innerHTML =
            '';

          drawPreview(
            els.canvas,
            state
          );
        };

        state.image.src =
          state.imageUrl;
      }
    );

    els.canvas.addEventListener(
      'pointerdown',
      (event) =>
        pointerDown(
          event,
          els.canvas,
          state
        )
    );

    els.canvas.addEventListener(
      'pointermove',
      (event) =>
        pointerMove(
          event,
          els.canvas,
          state
        )
    );

    els.canvas.addEventListener(
      'pointerup',
      (event) =>
        pointerUp(
          event,
          els.canvas,
          state
        )
    );

    els.canvas.addEventListener(
      'pointercancel',
      (event) =>
        pointerUp(
          event,
          els.canvas,
          state
        )
    );

    els.analyse.addEventListener(
      'click',
      async () => {
        if (
          !state.image ||
          !state.selection
        ) {
          return;
        }

        try {
          els.analyse.disabled =
            true;

          setStatus(
            els,
            'Analysing colours…'
          );

          const extracted =
            extractImagePalette(
              els.canvas,
              state.selection
            );

          state.basePalette =
            buildOklabBasePalette(
              extracted
            );

          state.sourceProductId =
            null;
          state.sourceType =
            'upload';
          state.sourceSku =
            '';
          state.sourceTitle =
            'Your uploaded fabric';

          resetResults(state);

          trackMatcherStart(
            state
          );

          const preview =
            createSelectionPreview(
              els.canvas,
              state
            );

          renderSource(
            els,
            preview ||
              state.imageUrl,
            'Your uploaded fabric',
            null,
            null,
            state
          );

          els.workspace.hidden =
            true;

          await updateMatches(
            els,
            state
          );
        } catch (error) {
          console.error(error);

          setStatus(
            els,
            'Sorry, this image could not be analysed.'
          );
        } finally {
          els.analyse.disabled =
            false;
        }
      }
    );
  }

  function prepareCanvas(
    canvas,
    image
  ) {
    const maxWidth = 900;
    const maxHeight = 650;

    const scale = Math.min(
      maxWidth /
        image.naturalWidth,

      maxHeight /
        image.naturalHeight,

      1
    );

    canvas.width = Math.max(
      1,
      Math.round(
        image.naturalWidth *
          scale
      )
    );

    canvas.height = Math.max(
      1,
      Math.round(
        image.naturalHeight *
          scale
      )
    );
  }

  function initialSelection(
    canvas
  ) {
    return {
      x:
        canvas.width *
        0.08,

      y:
        canvas.height *
        0.08,

      w:
        canvas.width *
        0.84,

      h:
        canvas.height *
        0.84
    };
  }

  function drawPreview(
    canvas,
    state
  ) {
    if (
      !state.image ||
      !state.selection
    ) {
      return;
    }

    const ctx =
      canvas.getContext(
        '2d',
        {
          willReadFrequently:
            true
        }
      );

    const s =
      state.selection;

    ctx.clearRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.drawImage(
      state.image,
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.save();

    ctx.fillStyle =
      'rgba(0,0,0,.34)';

    ctx.fillRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.clearRect(
      s.x,
      s.y,
      s.w,
      s.h
    );

    ctx.drawImage(
      state.image,

      s.x /
        canvas.width *
        state.image.naturalWidth,

      s.y /
        canvas.height *
        state.image.naturalHeight,

      s.w /
        canvas.width *
        state.image.naturalWidth,

      s.h /
        canvas.height *
        state.image.naturalHeight,

      s.x,
      s.y,
      s.w,
      s.h
    );

    ctx.strokeStyle =
      '#ffffff';

    ctx.lineWidth =
      2;

    ctx.strokeRect(
      s.x,
      s.y,
      s.w,
      s.h
    );

    selectionHandles(
      s
    ).forEach(
      (handle) => {
        ctx.fillStyle =
          '#ffffff';

        ctx.fillRect(
          handle.x - 5,
          handle.y - 5,
          10,
          10
        );

        ctx.strokeStyle =
          '#231f20';

        ctx.strokeRect(
          handle.x - 5,
          handle.y - 5,
          10,
          10
        );
      }
    );

    ctx.restore();
  }

  function selectionHandles(
    selection
  ) {
    return [
      {
        name: 'nw',
        x: selection.x,
        y: selection.y
      },
      {
        name: 'ne',
        x:
          selection.x +
          selection.w,
        y:
          selection.y
      },
      {
        name: 'sw',
        x:
          selection.x,
        y:
          selection.y +
          selection.h
      },
      {
        name: 'se',
        x:
          selection.x +
          selection.w,
        y:
          selection.y +
          selection.h
      }
    ];
  }

  function pointerPoint(
    event,
    canvas
  ) {
    const rect =
      canvas.getBoundingClientRect();

    return {
      x:
        (
          event.clientX -
          rect.left
        ) *
        canvas.width /
        rect.width,

      y:
        (
          event.clientY -
          rect.top
        ) *
        canvas.height /
        rect.height
    };
  }

  function pointerDown(
    event,
    canvas,
    state
  ) {
    if (!state.selection) {
      return;
    }

    const point =
      pointerPoint(
        event,
        canvas
      );

    const s =
      state.selection;

    const handle =
      selectionHandles(
        s
      ).find(
        (candidate) =>
          Math.abs(
            candidate.x -
            point.x
          ) <= 14 &&
          Math.abs(
            candidate.y -
            point.y
          ) <= 14
      );

    if (handle) {
      state.drag = {
        type: 'resize',
        handle:
          handle.name,
        start:
          point,
        selection: {
          ...s
        }
      };
    } else if (
      point.x >= s.x &&
      point.x <=
        s.x + s.w &&
      point.y >= s.y &&
      point.y <=
        s.y + s.h
    ) {
      state.drag = {
        type: 'move',
        start:
          point,
        selection: {
          ...s
        }
      };
    } else {
      state.drag = {
        type: 'new',
        start:
          point
      };

      state.selection = {
        x:
          point.x,
        y:
          point.y,
        w: 1,
        h: 1
      };
    }

    canvas.setPointerCapture(
      event.pointerId
    );
  }

  function pointerMove(
    event,
    canvas,
    state
  ) {
    if (!state.drag) return;

    const point =
      pointerPoint(
        event,
        canvas
      );

    if (
      state.drag.type ===
      'move'
    ) {
      const source =
        state.drag.selection;

      const dx =
        point.x -
        state.drag.start.x;

      const dy =
        point.y -
        state.drag.start.y;

      state.selection = {
        ...source,

        x: clamp(
          source.x + dx,
          0,
          canvas.width -
            source.w
        ),

        y: clamp(
          source.y + dy,
          0,
          canvas.height -
            source.h
        )
      };
    } else if (
      state.drag.type ===
      'resize'
    ) {
      state.selection =
        resizeSelection(
          state.drag.selection,
          state.drag.handle,
          point,
          canvas
        );
    } else {
      state.selection =
        normaliseSelection(
          state.drag.start,
          point,
          canvas
        );
    }

    drawPreview(
      canvas,
      state
    );
  }

  function pointerUp(
    event,
    canvas,
    state
  ) {
    if (!state.drag) return;

    state.drag =
      null;

    if (
      canvas.hasPointerCapture(
        event.pointerId
      )
    ) {
      canvas.releasePointerCapture(
        event.pointerId
      );
    }

    drawPreview(
      canvas,
      state
    );
  }

  function resizeSelection(
    source,
    handle,
    point,
    canvas
  ) {
    let x1 =
      source.x;

    let y1 =
      source.y;

    let x2 =
      source.x +
      source.w;

    let y2 =
      source.y +
      source.h;

    if (
      handle.includes(
        'w'
      )
    ) {
      x1 = point.x;
    }

    if (
      handle.includes(
        'e'
      )
    ) {
      x2 = point.x;
    }

    if (
      handle.includes(
        'n'
      )
    ) {
      y1 = point.y;
    }

    if (
      handle.includes(
        's'
      )
    ) {
      y2 = point.y;
    }

    return normaliseSelection(
      {
        x: x1,
        y: y1
      },

      {
        x: x2,
        y: y2
      },

      canvas
    );
  }

  function normaliseSelection(
    a,
    b,
    canvas
  ) {
    const x1 =
      clamp(
        Math.min(
          a.x,
          b.x
        ),
        0,
        canvas.width
      );

    const y1 =
      clamp(
        Math.min(
          a.y,
          b.y
        ),
        0,
        canvas.height
      );

    const x2 =
      clamp(
        Math.max(
          a.x,
          b.x
        ),
        0,
        canvas.width
      );

    const y2 =
      clamp(
        Math.max(
          a.y,
          b.y
        ),
        0,
        canvas.height
      );

    return {
      x: x1,
      y: y1,

      w: Math.max(
        20,
        x2 - x1
      ),

      h: Math.max(
        20,
        y2 - y1
      )
    };
  }

  function createSelectionPreview(
    canvas,
    state
  ) {
    if (
      !state.image ||
      !state.selection
    ) {
      return '';
    }

    try {
      const s =
        state.selection;

      const sx =
        s.x /
        canvas.width *
        state.image.naturalWidth;

      const sy =
        s.y /
        canvas.height *
        state.image.naturalHeight;

      const sw =
        s.w /
        canvas.width *
        state.image.naturalWidth;

      const sh =
        s.h /
        canvas.height *
        state.image.naturalHeight;

      const preview =
        document.createElement(
          'canvas'
        );

      const max = 700;

      const scale =
        Math.min(
          1,
          max /
          Math.max(
            sw,
            sh
          )
        );

      preview.width =
        Math.max(
          1,
          Math.round(
            sw * scale
          )
        );

      preview.height =
        Math.max(
          1,
          Math.round(
            sh * scale
          )
        );

      const ctx =
        preview.getContext(
          '2d'
        );

      ctx.drawImage(
        state.image,
        sx,
        sy,
        sw,
        sh,
        0,
        0,
        preview.width,
        preview.height
      );

      return preview.toDataURL(
        'image/jpeg',
        0.88
      );
    } catch {
      return '';
    }
  }

  function extractImagePalette(
    canvas,
    selection
  ) {
    const ctx =
      canvas.getContext(
        '2d',
        {
          willReadFrequently:
            true
        }
      );

    const x =
      Math.max(
        0,
        Math.floor(
          selection.x
        )
      );

    const y =
      Math.max(
        0,
        Math.floor(
          selection.y
        )
      );

    const w =
      Math.max(
        1,
        Math.min(
          canvas.width -
          x,
          Math.floor(
            selection.w
          )
        )
      );

    const h =
      Math.max(
        1,
        Math.min(
          canvas.height -
          y,
          Math.floor(
            selection.h
          )
        )
      );

    const image =
      ctx.getImageData(
        x,
        y,
        w,
        h
      );

    const buckets =
      new Map();

    const step =
      Math.max(
        1,
        Math.floor(
          Math.sqrt(
            w *
            h /
            40000
          )
        )
      );

    for (
      let py = 0;
      py < h;
      py += step
    ) {
      for (
        let px = 0;
        px < w;
        px += step
      ) {
        const index =
          (
            py *
            w +
            px
          ) *
          4;

        if (
          image.data[
            index + 3
          ] < 30
        ) {
          continue;
        }

        const r =
          image.data[
            index
          ];

        const g =
          image.data[
            index + 1
          ];

        const b =
          image.data[
            index + 2
          ];

        const q = 16;

        const qr =
          Math.min(
            255,
            Math.round(
              r / q
            ) * q
          );

        const qg =
          Math.min(
            255,
            Math.round(
              g / q
            ) * q
          );

        const qb =
          Math.min(
            255,
            Math.round(
              b / q
            ) * q
          );

        const key =
          `${qr},${qg},${qb}`;

        const bucket =
          buckets.get(
            key
          ) || {
            count: 0,
            r: 0,
            g: 0,
            b: 0
          };

        bucket.count += 1;
        bucket.r += r;
        bucket.g += g;
        bucket.b += b;

        buckets.set(
          key,
          bucket
        );
      }
    }

    const total =
      [
        ...buckets.values()
      ].reduce(
        (
          sum,
          bucket
        ) =>
          sum +
          bucket.count,
        0
      ) || 1;

    return [
      ...buckets.values()
    ]
      .map(
        (bucket) => ({
          hex:
            rgbToHex(
              bucket.r /
                bucket.count,

              bucket.g /
                bucket.count,

              bucket.b /
                bucket.count
            ),

          percentage:
            bucket.count /
            total *
            100
        })
      )
      .filter(
        (colour) =>
          colour.percentage >=
          0.5
      )
      .sort(
        (a, b) =>
          b.percentage -
          a.percentage
      )
      .slice(
        0,
        32
      );
  }

  async function loadProducts(
    state
  ) {
    if (state.products) {
      return state.products;
    }

    if (
      state.productsPromise
    ) {
      return state.productsPromise;
    }

    state.productsPromise =
      (async () => {
        const firstPage =
          await fetchColourDataPage(
            'all',
            1
          );

        const totalPages =
          Math.max(
            1,
            Number(
              firstPage.pages
            ) || 1
          );

        const pages =
          [
            firstPage
          ];

        if (
          totalPages > 1
        ) {
          const rest =
            await Promise.all(
              Array.from(
                {
                  length:
                    totalPages -
                    1
                },

                (
                  _,
                  index
                ) =>
                  fetchColourDataPage(
                    'all',
                    index +
                      2
                  )
              )
            );

          pages.push(
            ...rest
          );
        }

        const products =
          [];

        pages.forEach(
          (page) => {
            (
              page.products ||
              []
            ).forEach(
              (raw) => {
                // Out-of-stock products are excluded from the matcher dataset entirely.
                // This keeps them out of source search, matching calculations and results.
                if (raw.available === false) {
                  return;
                }

                const palette =
                  parsePalette(
                    raw.palette
                  );

                const display =
                  normalisePaletteColours(
                    palette.colours ||
                    []
                  );

                const analysis =
                  normalisePaletteColours(
                    Array.isArray(
                      palette.analysis_colours
                    ) &&
                    palette.analysis_colours.length
                      ? palette.analysis_colours
                      : palette.colours ||
                        []
                  );

                if (
                  !display.length &&
                  !analysis.length
                ) {
                  return;
                }

                const single =
                  raw.single_colour_family ===
                    true ||
                  palette.single_colour_family ===
                    true;

                const matchColours =
                  single &&
                  display.length
                    ? [
                        withOklab(
                          display[0]
                        )
                      ]
                    : buildOklabBasePalette(
                        analysis
                      ).map(
                        withOklab
                      );

                const compatibilityColours =
                  (
                    analysis.length
                      ? analysis
                      : display
                  )
                    .filter(
                      (colour) =>
                        colour.percentage >=
                        MATCHING.analysisMinPercentage
                    )
                    .map(
                      withOklab
                    );

                products.push(
                  {
                    ...raw,

                    id:
                      String(
                        raw.id ||
                        ''
                      ),

                    title:
                      String(
                        raw.title ||
                        ''
                      ),

                    sku:
                      String(
                        raw.sku ||
                        ''
                      ),

                    url:
                      String(
                        raw.url ||
                        ''
                      ),

                    image:
                      String(
                        raw.image ||
                        ''
                      ),

                    sourceImage:
                      String(
                        raw.source_image ||
                        raw.image ||
                        ''
                      ),

                    collections:
                      Array.isArray(
                        raw.collections
                      )
                        ? raw.collections
                        : [],

                    cardDetails:
                      normaliseCardDetails(
                        raw.card_details
                      ),

                    single_colour_family:
                      single,

                    paletteColours:
                      display,

                    analysisColours:
                      analysis,

                    matchColours,

                    compatibilityColours
                  }
                );
              }
            );
          }
        );

        state.products =
          products;

        return products;
      })();

    try {
      return await state.productsPromise;
    } finally {
      state.productsPromise =
        null;
    }
  }

  async function fetchColourDataPage(
    handle,
    page
  ) {
    const root =
      window.Shopify
        ?.routes
        ?.root ||
      '/';

    const url =
      new URL(
        `${root}collections/${encodeURIComponent(
          handle
        )}`,

        window.location.origin
      );

    url.searchParams.set(
      'view',
      'kf-colour-data'
    );

    url.searchParams.set(
      'page',
      String(
        page
      )
    );

    const currentUrl =
      new URL(
        window.location.href
      );

    const previewThemeId =
      currentUrl.searchParams.get(
        'preview_theme_id'
      ) ||
      window.Shopify
        ?.theme
        ?.id;

    if (
      window.Shopify
        ?.designMode &&
      previewThemeId
    ) {
      url.searchParams.set(
        'preview_theme_id',
        String(
          previewThemeId
        )
      );
    }

    const response =
      await fetch(
        url.toString(),
        {
          credentials:
            'same-origin',

          cache:
            'no-store'
        }
      );

    if (!response.ok) {
      throw new Error(
        `Colour data request failed for ${handle}, page ${page}: ${response.status}`
      );
    }

    return response.json();
  }

  async function loadBasicProductIds(
    state
  ) {
    if (
      state.basicProductIds
    ) {
      return state.basicProductIds;
    }

    if (
      state.basicProductsPromise
    ) {
      return state.basicProductsPromise;
    }

    state.basicProductsPromise =
      (async () => {
        const ids =
          new Set();

        for (
          const handle of
          state.basicCollections
        ) {
          let page = 1;
          let totalPages = 1;

          do {
            const data =
              await fetchColourDataPage(
                handle,
                page
              );

            totalPages =
              Math.max(
                1,
                Number(
                  data.pages
                ) || 1
              );

            (
              data.products ||
              []
            ).forEach(
              (product) => {
                ids.add(
                  String(
                    product.id
                  )
                );
              }
            );

            page += 1;
          } while (
            page <=
            totalPages
          );
        }

        state.basicProductIds =
          ids;

        return ids;
      })();

    try {
      return await state.basicProductsPromise;
    } finally {
      state.basicProductsPromise =
        null;
    }
  }

  function normaliseSearchText(
    value
  ) {
    return String(
      value ||
      ''
    )
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function parsePalette(
    value
  ) {
    if (
      value &&
      typeof value ===
      'object'
    ) {
      return value;
    }

    if (
      typeof value ===
      'string'
    ) {
      try {
        return JSON.parse(
          value
        );
      } catch {
        return {};
      }
    }

    return {};
  }

  function normalisePaletteColours(
    colours
  ) {
    return (
      Array.isArray(
        colours
      )
        ? colours
        : []
    )
      .filter(
        (colour) =>
          typeof colour?.hex ===
            'string' &&
          /^#[0-9a-f]{6}$/i.test(
            colour.hex
          )
      )
      .map(
        (colour) => ({
          hex:
            colour.hex.toUpperCase(),

          percentage:
            Math.max(
              0,
              Number(
                colour.percentage
              ) || 0
            )
        })
      );
  }

  function renderSuggestions(
    products,
    query,
    els,
    state
  ) {
    const needle =
      normaliseSearchText(
        query
      );

    const tokens =
      needle
        .split(/\s+/)
        .filter(Boolean);

    const ranked =
      products
        .filter(
          (product) =>
            product.available !== false
        )
        .map(
          (product) => {
            const sku =
              normaliseSearchText(
                product.sku
              );

            const title =
              normaliseSearchText(
                product.title
              );

            const details =
              product.cardDetails ||
              {};

            const searchableDetails =
              normaliseSearchText(
                [
                  product.type,
                  details.productType,
                  details.composition,
                  details.structure
                ]
                  .filter(Boolean)
                  .join(' ')
              );

            const searchText =
              `${title} ${sku} ${searchableDetails}`.trim();

            const allTokensMatch =
              tokens.length > 0 &&
              tokens.every(
                (token) =>
                  searchText.includes(
                    token
                  )
              );

            let rank = 99;

            if (
              sku ===
              needle
            ) {
              rank = 0;
            } else if (
              sku.startsWith(
                needle
              )
            ) {
              rank = 1;
            } else if (
              sku.includes(
                needle
              )
            ) {
              rank = 2;
            } else if (
              title ===
              needle
            ) {
              rank = 3;
            } else if (
              title.startsWith(
                needle
              )
            ) {
              rank = 4;
            } else if (
              title.includes(
                needle
              )
            ) {
              rank = 5;
            } else if (
              allTokensMatch
            ) {
              rank = 6;
            }

            return {
              product,
              rank
            };
          }
        )
        .filter(
          (item) =>
            item.rank <
            99
        )
        .sort(
          (a, b) =>
            a.rank -
              b.rank ||
            a.product.title.localeCompare(
              b.product.title
            )
        )
        .slice(
          0,
          8
        );

    els.suggestions.innerHTML =
      '';

    if (!ranked.length) {
      const empty =
        document.createElement(
          'div'
        );

      empty.className =
        'kf-colour-matcher__suggestion';

      empty.textContent =
        'No fabric found.';

      els.suggestions.appendChild(
        empty
      );

      els.suggestions.hidden =
        false;

      scheduleZeroResultTracking(
        query,
        els,
        state
      );

      return;
    }

    if (
      state.zeroResultTimer
    ) {
      window.clearTimeout(
        state.zeroResultTimer
      );
      state.zeroResultTimer =
        null;
    }

    ranked.forEach(
      ({
        product
      }) => {
        const button =
          document.createElement(
            'button'
          );

        button.type =
          'button';

        button.className =
          'kf-colour-matcher__suggestion';

        if (
          product.image
        ) {
          const image =
            document.createElement(
              'img'
            );

          image.className =
            'kf-colour-matcher__suggestion-image';

          image.src =
            product.image;

          image.alt =
            '';

          image.loading =
            'lazy';

          button.appendChild(
            image
          );
        }

        const copy =
          document.createElement(
            'span'
          );

        const title =
          document.createElement(
            'strong'
          );

        title.textContent =
          product.title;

        const meta =
          document.createElement(
            'span'
          );

        meta.className =
          'kf-colour-matcher__suggestion-meta';

        meta.textContent =
          product.sku
            ? `SKU ${product.sku}`
            : 'Fabric';

        copy.append(
          title,
          meta
        );

        button.appendChild(
          copy
        );

        button.addEventListener(
          'click',
          async () => {
            const searchQuery =
              els.search.value;

            els.search.value =
              product.sku ||
              product.title;

            els.suggestions.hidden =
              true;

            state.sourceProductId =
              product.id;
            state.sourceType =
              'product';
            state.sourceSku =
              product.sku ||
              '';
            state.sourceTitle =
              product.title ||
              '';

            trackMatcherSearch(
              searchQuery,
              product,
              state
            );

            trackMatcherStart(
              state
            );

            state.basePalette =
              product.single_colour_family &&
              product.paletteColours.length
                ? [
                    {
                      ...product.paletteColours[0]
                    }
                  ]
                : buildOklabBasePalette(
                    product.analysisColours
                  );

            resetResults(
              state
            );

            renderSource(
              els,
              product.sourceImage || product.image,
              product.title,
              product.cardDetails,
              product,
              state
            );

            await updateMatches(
              els,
              state
            );
          }
        );

        els.suggestions.appendChild(
          button
        );
      }
    );

    els.suggestions.hidden =
      false;
  }

  async function updateMatches(
    els,
    state
  ) {
    const selected =
      state.basePalette.map(
        withOklab
      );

    if (!selected.length) {
      return;
    }

    try {
      setStatus(
        els,
        'Finding coordinating solids & basics…'
      );

      const products =
        await loadProducts(
          state
        );

      if (
        state.basicCollections.length &&
        !state.basicProductIds
      ) {
        await loadBasicProductIds(
          state
        );
      }

      const candidates =
        products.filter(
          (product) => {
            if (product.available === false) {
              return false;
            }

            if (
              product.id ===
              String(
                state.sourceProductId ||
                ''
              )
            ) {
              return false;
            }

            if (
              state.basicCollections.length
            ) {
              return (
                state.basicProductIds?.has(
                  product.id
                ) === true
              );
            }

            return true;
          }
        );

      state.rankedResults =
        buildColourLaneRanking(
          selected,
          candidates,
          candidates.length
        );

      renderResults(
        els,
        state
      );

      setStatus(
        els,
        ''
      );
    } catch (error) {
      console.error(
        error
      );

      setStatus(
        els,
        'Sorry, the fabric matches could not be loaded.'
      );
    }
  }

  function buildColourLaneRanking(
    sourceColours,
    products,
    limit
  ) {
    if (
      !sourceColours.length ||
      !products.length
    ) {
      return [];
    }

    // The existing 70/30 matcher remains the base score.
    // Multicolour validation is calculated once per candidate/source pair
    // and only adds a post-score penalty for genuinely complex candidates
    // with a large amount of colour that the source palette cannot explain.
    const candidateMetrics =
      new Map();

    products.forEach(
      (product) => {
        candidateMetrics.set(
          product.id,
          {
            compatibility:
              wholeFabricCompatibilityScore(
                product,
                sourceColours
              ),
            multicolour:
              multicolourValidationMetrics(
                product,
                sourceColours
              )
          }
        );
      }
    );

    const lanes =
      sourceColours.map(
        (
          sourceColour
        ) =>
          products
            .map(
              (
                product
              ) => {
                let bestDistance =
                  Infinity;

                let matchedColour =
                  null;

                for (
                  const candidateColour of
                  product.matchColours
                ) {
                  const distance =
                    oklabDistance(
                      sourceColour.oklab,
                      candidateColour.oklab
                    );

                  if (
                    distance <
                    bestDistance
                  ) {
                    bestDistance =
                      distance;

                    matchedColour =
                      candidateColour;
                  }
                }

                const metrics =
                  candidateMetrics.get(
                    product.id
                  );

                const compatibility =
                  metrics?.compatibility ??
                  Infinity;

                const multicolour =
                  metrics?.multicolour ||
                  {
                    eligible: false,
                    unsupportedMass: 0,
                    penalty: 0
                  };

                const baseScore =
                  bestDistance *
                    MATCHING.laneWeight +
                  compatibility *
                    MATCHING.wholeFabricWeight;

                const score =
                  baseScore +
                  multicolour.penalty;

                return {
                  product,
                  sourceColour,
                  matchedColour,
                  laneDistance:
                    bestDistance,
                  compatibility,
                  baseScore,
                  multicolourValidationEligible:
                    multicolour.eligible,
                  unsupportedCandidateMass:
                    multicolour.unsupportedMass,
                  multicolourPenalty:
                    multicolour.penalty,
                  score
                };
              }
            )
            .sort(
              (
                a,
                b
              ) =>
                a.score -
                  b.score ||
                a.laneDistance -
                  b.laneDistance ||
                a.product.title.localeCompare(
                  b.product.title
                )
            )
      );

    const results =
      [];

    const usedIds =
      new Set();

    const positions =
      new Array(
        lanes.length
      ).fill(
        0
      );

    let progress =
      true;

    while (
      results.length <
      limit &&
      progress
    ) {
      progress =
        false;

      for (
        let laneIndex =
          0;
        laneIndex <
          lanes.length &&
        results.length <
          limit;
        laneIndex += 1
      ) {
        const lane =
          lanes[
            laneIndex
          ];

        while (
          positions[
            laneIndex
          ] <
          lane.length
        ) {
          const entry =
            lane[
              positions[
                laneIndex
              ]++
            ];

          if (
            usedIds.has(
              entry.product.id
            )
          ) {
            continue;
          }

          usedIds.add(
            entry.product.id
          );

          results.push(
            entry
          );

          progress =
            true;

          break;
        }
      }
    }

    return results;
  }

  function multicolourValidationMetrics(
    product,
    sourceColours
  ) {
    if (
      !isMulticolourValidationCandidate(
        product
      )
    ) {
      return {
        eligible: false,
        unsupportedMass: 0,
        penalty: 0
      };
    }

    const unsupportedMass =
      unsupportedCandidateColourMass(
        product,
        sourceColours
      );

    return {
      eligible: true,
      unsupportedMass,
      penalty:
        Math.max(
          0,
          unsupportedMass -
            MULTICOLOUR_VALIDATION.unsupportedGrace
        ) *
        MULTICOLOUR_VALIDATION.penaltyWeight
    };
  }

  function isMulticolourValidationCandidate(
    product
  ) {
    const candidateColours =
      product.compatibilityColours?.length
        ? product.compatibilityColours
        : product.matchColours;

    if (
      !candidateColours?.length
    ) {
      return false;
    }

    const colours =
      candidateColours
        .filter(
          (colour) =>
            Number(
              colour.percentage
            ) >=
            MULTICOLOUR_VALIDATION.familyMinPercentage
        )
        .sort(
          (a, b) =>
            Number(
              b.percentage
            ) -
            Number(
              a.percentage
            )
        );

    let neutralMass =
      0;

    const families =
      [];

    colours.forEach(
      (colour) => {
        const lab =
          colour.oklab ||
          hexToOklab(
            colour.hex
          );

        const chroma =
          Math.sqrt(
            lab.a *
              lab.a +
            lab.b *
              lab.b
          );

        const weight =
          Math.max(
            0,
            Number(
              colour.percentage
            ) || 0
          );

        if (
          chroma <
          MULTICOLOUR_VALIDATION.neutralChromaThreshold
        ) {
          neutralMass +=
            weight;
          return;
        }

        const hue =
          (
            Math.atan2(
              lab.b,
              lab.a
            ) *
              180 /
              Math.PI +
            360
          ) %
          360;

        let bestIndex =
          -1;

        let bestDistance =
          Infinity;

        families.forEach(
          (family, index) => {
            let distance =
              Math.abs(
                hue -
                  family.hue
              );

            distance =
              Math.min(
                distance,
                360 -
                  distance
              );

            if (
              distance <=
                MULTICOLOUR_VALIDATION.hueMergeDegrees &&
              distance <
                bestDistance
            ) {
              bestIndex =
                index;
              bestDistance =
                distance;
            }
          }
        );

        if (
          bestIndex <
          0
        ) {
          families.push(
            {
              hue,
              mass: weight
            }
          );
          return;
        }

        const family =
          families[
            bestIndex
          ];

        const newMass =
          family.mass +
          weight;

        const x =
          Math.cos(
            family.hue *
              Math.PI /
              180
          ) *
            family.mass +
          Math.cos(
            hue *
              Math.PI /
              180
          ) *
            weight;

        const y =
          Math.sin(
            family.hue *
              Math.PI /
              180
          ) *
            family.mass +
          Math.sin(
            hue *
              Math.PI /
              180
          ) *
            weight;

        family.hue =
          (
            Math.atan2(
              y,
              x
            ) *
              180 /
              Math.PI +
            360
          ) %
          360;

        family.mass =
          newMass;
      }
    );

    const familyMasses =
      families
        .map(
          (family) =>
            family.mass
        );

    if (
      neutralMass >=
      MULTICOLOUR_VALIDATION.familyMinPercentage
    ) {
      familyMasses.push(
        neutralMass
      );
    }

    familyMasses.sort(
      (a, b) =>
        b -
        a
    );

    const secondaryMass =
      familyMasses
        .slice(
          1
        )
        .reduce(
          (sum, value) =>
            sum +
            value,
          0
        );

    return (
      familyMasses.length >=
        MULTICOLOUR_VALIDATION.minFamilies &&
      secondaryMass >=
        MULTICOLOUR_VALIDATION.minSecondaryFamilyMass
    );
  }

  function unsupportedCandidateColourMass(
    product,
    sourceColours
  ) {
    const candidateColours =
      product.compatibilityColours?.length
        ? product.compatibilityColours
        : product.matchColours;

    if (
      !candidateColours?.length ||
      !sourceColours.length
    ) {
      return 1;
    }

    let totalWeight =
      0;

    let unsupportedWeight =
      0;

    candidateColours.forEach(
      (candidateColour) => {
        const weight =
          Math.max(
            0,
            Number(
              candidateColour.percentage
            ) || 0
          );

        if (
          weight <
          MATCHING.analysisMinPercentage
        ) {
          return;
        }

        totalWeight +=
          weight;

        let nearestSourceDistance =
          Infinity;

        sourceColours.forEach(
          (sourceColour) => {
            const distance =
              oklabDistance(
                candidateColour.oklab,
                sourceColour.oklab
              );

            if (
              distance <
              nearestSourceDistance
            ) {
              nearestSourceDistance =
                distance;
            }
          }
        );

        if (
          nearestSourceDistance >
          MULTICOLOUR_VALIDATION.representedDistance
        ) {
          unsupportedWeight +=
            weight;
        }
      }
    );

    if (
      !totalWeight
    ) {
      return 1;
    }

    return (
      unsupportedWeight /
      totalWeight
    );
  }

  function wholeFabricCompatibilityScore(
    product,
    sourceColours
  ) {
    const candidateColours =
      product.compatibilityColours?.length
        ? product.compatibilityColours
        : product.matchColours;

    if (
      !candidateColours?.length ||
      !sourceColours.length
    ) {
      return Infinity;
    }

    let weightedSquaredDistance =
      0;

    let totalWeight =
      0;

    for (
      const candidateColour of
      candidateColours
    ) {
      const weight =
        Math.max(
          0,
          Number(
            candidateColour.percentage
          ) || 0
        );

      if (
        weight <
        MATCHING.analysisMinPercentage
      ) {
        continue;
      }

      let nearestSourceDistance =
        Infinity;

      for (
        const sourceColour of
        sourceColours
      ) {
        const distance =
          oklabDistance(
            candidateColour.oklab,
            sourceColour.oklab
          );

        if (
          distance <
          nearestSourceDistance
        ) {
          nearestSourceDistance =
            distance;
        }
      }

      weightedSquaredDistance +=
        weight *
        nearestSourceDistance *
        nearestSourceDistance;

      totalWeight +=
        weight;
    }

    if (!totalWeight) {
      return Infinity;
    }

    return Math.sqrt(
      weightedSquaredDistance /
      totalWeight
    );
  }

  function renderResults(
    els,
    state
  ) {
    els.closeGrid.innerHTML =
      '';

    const visible =
      state.rankedResults
        .filter(
          ({
            product
          }) =>
            !state.dismissedIds.has(
              product.id
            )
        )
        .slice(
          0,
          state.resultsCount
        );

    if (!visible.length) {
      if (
        els.closeNote
      ) {
        els.closeNote.textContent =
          'No more matching fabrics to show.';

        els.closeNote.hidden =
          false;
      }

      if (
        els.results
      ) {
        els.results.hidden =
          false;
      }

      return;
    }

    if (
      els.closeNote
    ) {
      els.closeNote.textContent =
        '';

      els.closeNote.hidden =
        true;
    }

    visible.forEach(
      (
        {
          product
        },
        index
      ) => {
        els.closeGrid.appendChild(
          createProductCard(
            product,
            state,
            els,
            index
          )
        );
      }
    );

    trackResultsView(
      visible,
      state
    );

    if (
      els.results
    ) {
      els.results.hidden =
        false;
    }

    syncSourceCardHeight(
      els
    );
  }

  function createProductCard(
    product,
    state,
    els,
    index
  ) {
    const card =
      document.createElement(
        'product-card'
      );

    card.className =
      'kf-colour-matcher__card';

    card.dataset.productId =
      String(
        product.id ||
        ''
      );

    card.style.setProperty(
      '--quick-add-display',
      'flex'
    );

    card.style.setProperty(
      '--quick-add-mobile-display',
      state.mobileQuickAddEnabled
        ? 'flex'
        : 'none'
    );

    card.style.setProperty(
      '--quick-add-mobile-opacity',
      state.mobileQuickAddEnabled
        ? '1'
        : '0'
    );

    const link =
      document.createElement(
        'a'
      );

    link.className =
      'kf-colour-matcher__card-link';

    link.href =
      product.url;

    link.setAttribute(
      'ref',
      'productCardLink'
    );

    link.setAttribute(
      'aria-label',
      product.title ||
      'View fabric'
    );

    link.addEventListener(
      'click',
      () => {
        trackResultSelect(
          product,
          index,
          state
        );
      }
    );

    const imageWrap =
      document.createElement(
        'div'
      );

    imageWrap.className =
      'kf-colour-matcher__card-image-wrap';

    if (
      product.image
    ) {
      const image =
        document.createElement(
          'img'
        );

      image.className =
        'kf-colour-matcher__card-image';

      image.src =
        product.image;

      image.alt =
        product.title ||
        '';

      image.loading =
        'lazy';

      image.width =
        560;

      image.height =
        560;

      imageWrap.appendChild(
        image
      );
    }

    const body =
      document.createElement(
        'div'
      );

    body.className =
      'kf-colour-matcher__card-body';

    const title =
      document.createElement(
        'h4'
      );

    title.className =
      'kf-colour-matcher__card-title';

    title.textContent =
      product.title ||
      '';

    body.appendChild(
      title
    );

    const details =
      createCardDetailsElement(
        product.cardDetails
      );

    if (details) {
      body.appendChild(
        details
      );
    }

    link.append(
      imageWrap,
      body
    );

    card.appendChild(
      link
    );

    const footer =
      document.createElement(
        'div'
      );

    footer.className =
      'kf-colour-matcher__card-footer';

    const pricing =
      document.createElement(
        'div'
      );

    pricing.className =
      'kf-colour-matcher__card-pricing';

    const price =
      document.createElement(
        'span'
      );

    price.className =
      'kf-colour-matcher__card-price';

    price.textContent =
      product.price ||
      '';

    pricing.appendChild(
      price
    );

    const unitText =
      formatUnitPrice(
        product
      );

    if (
      unitText
    ) {
      const unit =
        document.createElement(
          'small'
        );

      unit.className =
        'kf-colour-matcher__card-unit-price';

      unit.textContent =
        unitText;

      pricing.appendChild(
        unit
      );
    }

    footer.appendChild(
      pricing
    );

    if (
      !product.available
    ) {
      const soldOut =
        document.createElement(
          'span'
        );

      soldOut.className =
        'kf-colour-matcher__sold-out';

      soldOut.textContent =
        'Currently unavailable';

      footer.appendChild(
        soldOut
      );
    } else if (
      state.quickAddEnabled
    ) {
      const quickAdd =
        createNativeQuickAdd(
          product,
          state,
          index
        );

      if (
        quickAdd
      ) {
        footer.appendChild(
          quickAdd
        );
      }
    }

    card.appendChild(
      footer
    );

    return card;
  }

  function formatUnitPrice(
    product
  ) {
    const unit =
      String(
        product.unit_reference_unit ||
        ''
      ).trim();

    if (!unit) {
      return '';
    }

    const rawValue =
      Number(
        product.unit_reference_value
      );

    const value =
      Number.isFinite(
        rawValue
      ) &&
      rawValue !==
        1
        ? String(
            rawValue
          )
        : '';

    const measurement =
      `${value}${unit}`;

    if (
      product.unit_price_matches_price
    ) {
      return `/${measurement}`;
    }

    if (
      product.unit_price
    ) {
      return `${product.unit_price}/${measurement}`;
    }

    return '';
  }

  function createNativeQuickAdd(
    product,
    state,
    index = null
  ) {
    const quickAdd =
      document.createElement(
        'quick-add-component'
      );

    quickAdd.className =
      'quick-add kf-colour-matcher__native-quick-add';

    quickAdd.setAttribute(
      'ref',
      'quickAdd'
    );

    quickAdd.dataset.productTitle =
      product.title ||
      '';

    quickAdd.dataset.productId =
      String(
        product.id ||
        ''
      );

    quickAdd.dataset.productOptionsCount =
      String(
        product.options_count ||
        1
      );

    quickAdd.dataset.usesSellingPlans =
      product.uses_selling_plans
        ? 'true'
        : 'false';

    quickAdd.dataset.rendersBothButtons =
      'false';

    const directAdd =
      Number(
        product.variants_count ||
        1
      ) === 1 &&
      !product.uses_selling_plans &&
      product.variant_id;

    quickAdd.dataset.quickAddButton =
      directAdd
        ? 'add'
        : 'choose';

    if (
      directAdd
    ) {
      const productForm =
        document.createElement(
          'product-form-component'
        );

      productForm.className =
        'quick-add__product-form-component';

      productForm.id =
        `KFMatcherProductFormComponent-${product.id}`;

      productForm.dataset.sectionId =
        'kf-colour-matcher';

      productForm.dataset.productId =
        String(
          product.id ||
          ''
        );

      productForm.setAttribute(
        'on:submit',
        '/handleSubmit'
      );

      const liveRegion =
        document.createElement(
          'div'
        );

      liveRegion.className =
        'visually-hidden';

      liveRegion.setAttribute(
        'aria-live',
        'assertive'
      );

      liveRegion.setAttribute(
        'role',
        'status'
      );

      liveRegion.setAttribute(
        'aria-atomic',
        'true'
      );

      liveRegion.setAttribute(
        'ref',
        'liveRegion'
      );

      const form =
        document.createElement(
          'form'
        );

      form.method =
        'post';

      form.action =
        `${window.Shopify?.routes?.root || '/'}cart/add`;

      form.enctype =
        'multipart/form-data';

      form.id =
        `KFMatcherQuickAdd-${product.id}`;

      const variant =
        document.createElement(
          'input'
        );

      variant.type =
        'hidden';

      variant.name =
        'id';

      variant.value =
        String(
          product.variant_id
        );

      variant.setAttribute(
        'ref',
        'variantId'
      );

      const quantity =
        document.createElement(
          'input'
        );

      quantity.type =
        'hidden';

      quantity.name =
        'quantity';

      quantity.value =
        '1';

      const addComponent =
        document.createElement(
          'add-to-cart-component'
        );

      addComponent.setAttribute(
        'ref',
        'addToCartButtonContainer'
      );

      addComponent.dataset.productVariantMedia =
        product.image ||
        '';

      addComponent.dataset.addToCartAnimation =
        'true';

      const button =
        document.createElement(
          'button'
        );

      button.type =
        'submit';

      button.name =
        'add';

      button.className =
        'button quick-add__button quick-add__button--add add-to-cart-button';

      button.setAttribute(
        'ref',
        'addToCartButton'
      );

      button.setAttribute(
        'on:click',
        '/handleClick'
      );

      button.setAttribute(
        'aria-label',
        `Add ${
          product.title ||
          'fabric'
        } to cart`
      );

      if (
        Number.isFinite(
          index
        )
      ) {
        button.addEventListener(
          'click',
          () => {
            markPendingMatcherAdd(
              product,
              index,
              state
            );
          }
        );
      }

      button.appendChild(
        createQuickAddIcon(
          state.addToCartIconUrl
        )
      );

      button.appendChild(
        document.createTextNode(
          'Add'
        )
      );

      const added =
        document.createElement(
          'span'
        );

      added.className =
        'add-to-cart__added';

      added.setAttribute(
        'aria-hidden',
        'true'
      );

      const tick =
        document.createElement(
          'span'
        );

      tick.className =
        'kf-colour-matcher__quick-added';

      tick.textContent =
        '✓';

      added.appendChild(
        tick
      );

      button.appendChild(
        added
      );

      addComponent.appendChild(
        button
      );

      form.append(
        variant,
        quantity,
        addComponent
      );

      productForm.append(
        liveRegion,
        form
      );

      quickAdd.appendChild(
        productForm
      );
    } else {
      const button =
        document.createElement(
          'button'
        );

      button.type =
        'button';

      button.className =
        'button quick-add__button quick-add__button--choose add-to-cart-button';

      button.name =
        'add';

      button.setAttribute(
        'on:click',
        'quick-add-component/handleClick'
      );

      button.setAttribute(
        'aria-label',
        `Choose options for ${
          product.title ||
          'fabric'
        }`
      );

      if (
        Number.isFinite(
          index
        )
      ) {
        button.addEventListener(
          'click',
          () => {
            markPendingMatcherAdd(
              product,
              index,
              state
            );
          }
        );
      }

      button.appendChild(
        createQuickAddIcon(
          state.addToCartIconUrl
        )
      );

      button.appendChild(
        document.createTextNode(
          'Choose'
        )
      );

      quickAdd.appendChild(
        button
      );
    }

    return quickAdd;
  }

  function createQuickAddIcon(
    url
  ) {
    const wrapper =
      document.createElement(
        'span'
      );

    wrapper.className =
      'svg-wrapper add-to-cart-icon';

    wrapper.setAttribute(
      'aria-hidden',
      'true'
    );

    if (
      url
    ) {
      const image =
        document.createElement(
          'img'
        );

      image.src =
        url;

      image.alt =
        '';

      image.width =
        20;

      image.height =
        20;

      wrapper.appendChild(
        image
      );
    } else {
      wrapper.textContent =
        '+';
    }

    return wrapper;
  }

  function buildOklabBasePalette(
    colours
  ) {
    const working =
      normalisePaletteColours(
        colours
      )
        .filter(
          (colour) =>
            colour.percentage >=
            MATCHING.analysisMinPercentage
        )
        .map(
          withOklab
        )
        .sort(
          (a, b) =>
            b.percentage -
            a.percentage
        );

    if (
      !working.length
    ) {
      return [];
    }

    const selected =
      [
        working[0]
      ];

    const remaining =
      working.slice(
        1
      );

    const distanceToSelected =
      (
        colour
      ) =>
        Math.min(
          ...selected.map(
            (
              candidate
            ) =>
              oklabDistance(
                colour.oklab,
                candidate.oklab
              )
          )
        );

    while (
      remaining.length &&
      selected.length <
      MATCHING.baseMaxColours
    ) {
      const missingImportant =
        working.filter(
          (
            colour
          ) =>
            colour.percentage >=
              MATCHING.baseForcePercentage &&
            distanceToSelected(
              colour
            ) >
              MATCHING.baseRepresentationDistance
        );

      const representedCoverage =
        working.reduce(
          (
            sum,
            colour
          ) => {
            return (
              sum +
              (
                distanceToSelected(
                  colour
                ) <=
                MATCHING.baseRepresentationDistance
                  ? colour.percentage
                  : 0
              )
            );
          },
          0
        );

      if (
        !missingImportant.length &&
        representedCoverage >=
          MATCHING.baseTargetCoverage
      ) {
        break;
      }

      let index =
        -1;

      if (
        missingImportant.length
      ) {
        const target =
          [
            ...missingImportant
          ].sort(
            (
              a,
              b
            ) =>
              b.percentage -
              a.percentage
          )[0];

        let bestDistance =
          Infinity;

        remaining.forEach(
          (
            colour,
            candidateIndex
          ) => {
            const distance =
              oklabDistance(
                colour.oklab,
                target.oklab
              );

            if (
              distance <
              bestDistance
            ) {
              bestDistance =
                distance;

              index =
                candidateIndex;
            }
          }
        );
      } else {
        const maxPercentage =
          working[0].percentage ||
          1;

        let bestScore =
          -Infinity;

        remaining.forEach(
          (
            colour,
            candidateIndex
          ) => {
            const support =
              colour.percentage /
              maxPercentage;

            const diversity =
              clamp(
                distanceToSelected(
                  colour
                ) /
                  0.12,
                0,
                1
              );

            const score =
              support *
                0.9 +
              diversity *
                0.1;

            if (
              score >
              bestScore
            ) {
              bestScore =
                score;

              index =
                candidateIndex;
            }
          }
        );
      }

      if (
        index <
        0
      ) {
        break;
      }

      selected.push(
        remaining.splice(
          index,
          1
        )[0]
      );
    }

    return selected.map(
      ({
        hex,
        percentage
      }) => ({
        hex,
        percentage
      })
    );
  }

  function withOklab(
    colour
  ) {
    return {
      ...colour,

      oklab:
        colour.oklab ||
        hexToOklab(
          colour.hex
        )
    };
  }

  function hexToOklab(
    hex
  ) {
    const value =
      String(
        hex ||
        ''
      ).replace(
        '#',
        ''
      );

    if (
      !/^[0-9a-f]{6}$/i.test(
        value
      )
    ) {
      return {
        L: 0,
        a: 0,
        b: 0
      };
    }

    return rgbToOklab(
      parseInt(
        value.slice(
          0,
          2
        ),
        16
      ),

      parseInt(
        value.slice(
          2,
          4
        ),
        16
      ),

      parseInt(
        value.slice(
          4,
          6
        ),
        16
      )
    );
  }

  function rgbToOklab(
    r,
    g,
    b
  ) {
    const linearise =
      (
        value
      ) => {
        const channel =
          value /
          255;

        return channel <=
          0.04045
          ? channel /
            12.92
          : Math.pow(
              (
                channel +
                0.055
              ) /
                1.055,
              2.4
            );
      };

    const R =
      linearise(
        r
      );

    const G =
      linearise(
        g
      );

    const B =
      linearise(
        b
      );

    const l =
      0.4122214708 *
        R +
      0.5363325363 *
        G +
      0.0514459929 *
        B;

    const m =
      0.2119034982 *
        R +
      0.6806995451 *
        G +
      0.1073969566 *
        B;

    const s =
      0.0883024619 *
        R +
      0.2817188376 *
        G +
      0.6299787005 *
        B;

    const lRoot =
      Math.cbrt(
        l
      );

    const mRoot =
      Math.cbrt(
        m
      );

    const sRoot =
      Math.cbrt(
        s
      );

    return {
      L:
        0.2104542553 *
          lRoot +
        0.7936177850 *
          mRoot -
        0.0040720468 *
          sRoot,

      a:
        1.9779984951 *
          lRoot -
        2.4285922050 *
          mRoot +
        0.4505937099 *
          sRoot,

      b:
        0.0259040371 *
          lRoot +
        0.7827717662 *
          mRoot -
        0.8086757660 *
          sRoot
    };
  }

  function oklabDistance(
    a,
    b
  ) {
    return Math.sqrt(
      Math.pow(
        a.L -
        b.L,
        2
      ) +
      Math.pow(
        a.a -
        b.a,
        2
      ) +
      Math.pow(
        a.b -
        b.b,
        2
      )
    );
  }

  function rgbToHex(
    r,
    g,
    b
  ) {
    return (
      '#' +
      [
        r,
        g,
        b
      ]
        .map(
          (
            value
          ) =>
            Math.round(
              clamp(
                value,
                0,
                255
              )
            )
              .toString(
                16
              )
              .padStart(
                2,
                '0'
              )
        )
        .join(
          ''
        )
        .toUpperCase()
    );
  }

  function clamp(
    value,
    min,
    max
  ) {
    return Math.min(
      max,
      Math.max(
        min,
        value
      )
    );
  }

  function boot() {
    document
      .querySelectorAll(
        '[data-kf-colour-matcher]'
      )
      .forEach(
        init
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    (
      event
    ) => {
      const section =
        event.target.querySelector?.(
          '[data-kf-colour-matcher]'
        ) ||
        (
          event.target.matches?.(
            '[data-kf-colour-matcher]'
          )
            ? event.target
            : null
        );

      if (
        section
      ) {
        init(
          section
        );
      }
    }
  );
})();